import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { storage } from '@/services/storage';
import { refreshSession } from '@/services/sessionRefresh';
import { setWsToken } from '@/services/wsToken';

export interface UserData {
    id: number;
    username: string;
    email?: string | null;
    name: string;
    rol: string;
}

interface AuthContextValue {
    user: UserData | null;
    checkSession: () => Promise<UserData | null>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<UserData | null>(null);

    const clearLocal = useCallback(() => {
        storage.deleteItem('userData');
        setWsToken(null);
        setUser(null);
    }, []);

    const checkSession = useCallback(async (): Promise<UserData | null> => {
        try {
            let res = await fetch('/me', { credentials: 'include' });

            if (res.status === 401 && (await refreshSession())) {
                res = await fetch('/me', { credentials: 'include' });
            }

            if (!res.ok) {
                clearLocal();
                return null;
            }

            const data = await res.json();
            setUser(data.user);
            storage.setItem('userData', JSON.stringify(data.user));
            if (data.wsToken) setWsToken(data.wsToken);
            return data.user;
        } catch {
            const userDataStr = storage.getItem('userData');
            if (userDataStr) {
                try {
                    const userData = JSON.parse(userDataStr) as UserData;
                    setUser(userData);
                    return userData;
                } catch {
                    /* corrupted */
                }
            }
            clearLocal();
            return null;
        }
    }, [clearLocal]);

    const logout = useCallback(async () => {
        try {
            await fetch('/logout', { method: 'POST', credentials: 'include' });
        } catch {
            /* best effort */
        }
        clearLocal();
    }, [clearLocal]);

    const value = useMemo(
        () => ({ user, checkSession, logout }),
        [user, checkSession, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return ctx;
}
